import { Plus } from "lucide-react"

import { ReactComponent as MaterialsEmptyState } from "../../../../assets/svg/materials-empty-state.svg"

import CustomSelect from "../../../../pages/ProjectDocumentation/components/CustomSelect.tsx"

import type { MaterialsAutofillAnalysisContext } from "../../../../services/materialsQuotationAutofillDraftService.ts"

import type { CategorySelectOption } from "./CalculadoraCatalogToolbar.tsx"

import MaterialsAutofillAnalysisCard, {
  type AutofillImportSummary,
} from "./MaterialsAutofillAnalysisCard.tsx"

import MaterialsCommerceSupplierCta from "./MaterialsCommerceSupplierCta.tsx"

import type { MaterialsQuotationFlow } from "../../../../utils/analytics/materialsQuotationFunnelAnalytics.ts"

import type { UserData } from "../../../../types/calculadoraDeMateriales.ts"

import styles from "../CalculadoraDeMateriales.module.scss"



export type { AutofillImportSummary } from "./MaterialsAutofillAnalysisCard.tsx"

export type BrowseCategoryItem = {

  id: string

  name: string

  count: number

}



export type CalculadoraMaterialsEmptyPanelProps = {

  showSecondEmptyState: boolean

  showCategoryBrowseSuggestions: boolean

  compactMaterialsCenter: boolean

  autofillImportSummary: AutofillImportSummary | null

  analysisContext: MaterialsAutofillAnalysisContext | null

  titleWhenEmpty: string

  subTitleWhenEmpty: string

  categoryOptions: CategorySelectOption[]

  selectedCategory: string

  toggleCategory: (categoryId: string) => void

  handleSearchLinkClick: () => void

  setShowAddCustomPopup: (v: boolean) => void

  topCategoriesForBrowse: BrowseCategoryItem[]

  onSelectBrowseCategory: (categoryId: string) => void

  totalCatalogMaterialsLabel: string

  isEstimacionDeCostos?: boolean

  estimationCatalogGapCount?: number

  onRequestQuotesFromEstimation?: () => void

  userData: UserData | null

  quotationFlow: MaterialsQuotationFlow

  edifyUserId?: string

}



const CalculadoraMaterialsEmptyPanel = ({

  showSecondEmptyState,

  showCategoryBrowseSuggestions,

  compactMaterialsCenter,

  autofillImportSummary,

  analysisContext,

  titleWhenEmpty,

  subTitleWhenEmpty,

  categoryOptions,

  selectedCategory,

  toggleCategory,

  handleSearchLinkClick,

  setShowAddCustomPopup,

  topCategoriesForBrowse,

  onSelectBrowseCategory,

  totalCatalogMaterialsLabel,

  isEstimacionDeCostos = false,

  estimationCatalogGapCount = 0,

  onRequestQuotesFromEstimation,

  userData,

  quotationFlow,

  edifyUserId = "",

}: CalculadoraMaterialsEmptyPanelProps) => {

  const formatMaterialCount = (count: number) =>

    count === 1 ? "1 material" : `${new Intl.NumberFormat("es-AR").format(count)} materiales`

  const showAnalysisCard =

    compactMaterialsCenter &&

    (autofillImportSummary != null ||

      Boolean(analysisContext?.summary) ||

      Boolean(analysisContext?.notesForUser) ||

      analysisContext?.documentRelevance === "unlikely_materials_list" ||

      analysisContext?.documentRelevance === "other_or_unclear")

  const hideOuterTitleWhenCompactAnalysis = compactMaterialsCenter && showAnalysisCard



  return (

    <div

      className={`${styles.container_body_categoryMaterials_empty} ${

        compactMaterialsCenter ? styles.container_body_categoryMaterials_emptyCompactBelowToolbar : ""

      } ${showCategoryBrowseSuggestions ? styles.container_body_categoryMaterials_emptyBrowseMode : ""}`}

    >

      {!showSecondEmptyState && !compactMaterialsCenter && (

        <div className={styles.container_body_categoryMaterials_empty_visual}>

          <MaterialsEmptyState className={styles.container_body_categoryMaterials_empty_icon} aria-hidden />

        </div>

      )}

      <div

        className={`${styles.container_body_categoryMaterials_empty_content} ${

          showSecondEmptyState || compactMaterialsCenter

            ? styles.container_body_categoryMaterials_empty_content_compact

            : ""

        }`}

      >

        {!hideOuterTitleWhenCompactAnalysis && !(showSecondEmptyState && showCategoryBrowseSuggestions) ? (

          <>

            <h2 className={styles.container_body_categoryMaterials_empty_title}>{titleWhenEmpty}</h2>

            <p className={styles.container_body_categoryMaterials_empty_text}>{subTitleWhenEmpty}</p>

          </>

        ) : null}

        {showAnalysisCard && autofillImportSummary ? (

          <MaterialsAutofillAnalysisCard

            analysisContext={analysisContext}

            autofillImportSummary={autofillImportSummary}

            isEstimacionDeCostos={isEstimacionDeCostos}

            estimationCatalogGapCount={estimationCatalogGapCount}

            onRequestQuotesFromEstimation={onRequestQuotesFromEstimation}

          />

        ) : null}

        {compactMaterialsCenter && !showCategoryBrowseSuggestions ? (

          <p className={styles.container_body_categoryMaterials_empty_nextStepsCompact} role="note">

            Para sumar más materiales usá la barra superior (categoría o búsqueda por nombre). Si no está en el

            catálogo, podés agregar un material manualmente.

          </p>

        ) : null}

        {compactMaterialsCenter && !showCategoryBrowseSuggestions ? (

          <button

            type="button"

            className={`${styles.container_body_categoryMaterials_empty_searchBtn} ${styles.customMaterialEmptyBtn} ${styles.container_body_categoryMaterials_empty_compactCta}`}

            onClick={() => setShowAddCustomPopup(true)}

          >

            <Plus size={16} style={{ minHeight: "1.6rem", minWidth: "1.6rem" }} />

            Agregar material manualmente

          </button>

        ) : null}

        {showCategoryBrowseSuggestions ? (

          <div className={styles.container_body_categoryMaterials_empty_categoryBrowse}>

            <h3 className={styles.container_body_categoryMaterials_empty_categoryBrowse_title}>

              Explorar por rubro

            </h3>

            <p className={styles.container_body_categoryMaterials_empty_categoryBrowse_intro}>

              Elegí un rubro para ver materiales y sumarlos a tu {isEstimacionDeCostos ? "estimación" : "cotización"}.

            </p>

            <div className={styles.container_body_categoryMaterials_empty_categoryBrowse_grid}>

              {topCategoriesForBrowse.map((category) => (

                <button

                  key={category.id}

                  type="button"

                  className={styles.container_body_categoryMaterials_empty_categoryBrowse_card}

                  onClick={() => onSelectBrowseCategory(category.id)}

                >

                  <span className={styles.container_body_categoryMaterials_empty_categoryBrowse_cardName}>

                    {category.name}

                  </span>

                  <span className={styles.container_body_categoryMaterials_empty_categoryBrowse_cardCount}>

                    {formatMaterialCount(category.count)}

                  </span>

                </button>

              ))}

            </div>

            <button

              type="button"

              className={styles.container_body_categoryMaterials_empty_categoryBrowse_customBtn}

              onClick={() => setShowAddCustomPopup(true)}

            >

              <Plus size={16} style={{ minHeight: "1.6rem", minWidth: "1.6rem" }} />

              Agregar material manualmente

            </button>

            <MaterialsCommerceSupplierCta

              userData={userData}

              flow={quotationFlow}

              edifyUserId={edifyUserId}

            />

          </div>

        ) : null}

        {!showSecondEmptyState && !compactMaterialsCenter ? (

          <>

            <div className={styles.container_body_categoryMaterials_empty_dropdown}>

              <CustomSelect

                value={selectedCategory}

                onChange={toggleCategory}

                options={categoryOptions}

                placeholder="Elegí una categoría para empezar"

                placement="top"

                searchable

                searchPlaceholder="Buscar categoría..."

              />

            </div>

            <div className={styles.container_body_categoryMaterials_empty_actions}>

              <button

                type="button"

                className={styles.container_body_categoryMaterials_empty_searchLink}

                onClick={handleSearchLinkClick}

              >

                ¿Sabés qué necesitás? Buscar por nombre

              </button>

              <button

                type="button"

                className={`${styles.container_body_categoryMaterials_empty_searchBtn} ${styles.customMaterialEmptyBtn}`}

                onClick={() => setShowAddCustomPopup(true)}

              >

                <Plus size={16} style={{ minHeight: "1.6rem", minWidth: "1.6rem" }} />

                Agregar material manualmente

              </button>

            </div>

          </>

        ) : null}

      </div>

    </div>

  )

}



export default CalculadoraMaterialsEmptyPanel


